import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { usePermissionCheck } from '../../contexts/PermissionContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import apiService from '../../services/api'
import { tokenStorage } from '../../utils/crypto'
import { getDocumentSecurityAccess } from '../../config/documentSecurity'
import {
  HiOutlineDocumentText,
  HiOutlinePlus,
  HiOutlineEye,
  HiOutlineTrash,
  HiOutlineMagnifyingGlass,
  HiOutlineArrowDownTray,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineFolder,
  HiOutlineShieldCheck,
  HiOutlineShieldExclamation,
  HiOutlineExclamationTriangle,
  HiOutlineCalendar,
  HiOutlineUser,
  HiOutlineClock,
  HiOutlineXMark,
  HiOutlineChatBubbleLeft,
  HiOutlineSpeakerWave,
} from 'react-icons/hi2'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import DocumentUploadModal from '../../components/documents/DocumentUploadModal'
import StrandedFilesBanner from '../../components/documents/StrandedFilesBanner'
import BlockedAction from '../../components/common/BlockedAction'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  IconButton,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
} from '../../components/ui'

// ───────── Security badge ──────────────────────────────────────────────
const SECURITY_LEVELS = {
  red:    { tone: 'danger',  icon: HiOutlineShieldExclamation,    labelKey: 'high' },
  yellow: { tone: 'warning', icon: HiOutlineExclamationTriangle, labelKey: 'medium' },
  green:  { tone: 'success', icon: HiOutlineShieldCheck,          labelKey: 'low' },
}

function SecurityBadge({ level }) {
  const { t } = useTranslation()
  const cfg = SECURITY_LEVELS[level] || SECURITY_LEVELS.green
  const Icon = cfg.icon
  return (
    <Badge tone={cfg.tone} size="sm">
      <Icon className="w-3 h-3" />
      {t(`documents.security.${cfg.labelKey}`)}
    </Badge>
  )
}

const TYPE_TONE = {
  document: 'bg-brand-soft text-brand',
  text:     'bg-success-soft text-success',
  voice:    'bg-info-soft text-info',
}

function DocumentTypeIcon({ type }) {
  if (type === 'document') return <HiOutlineDocumentText className="w-5 h-5" />
  if (type === 'text')     return <HiOutlineChatBubbleLeft className="w-5 h-5" />
  if (type === 'voice')    return <HiOutlineSpeakerWave className="w-5 h-5" />
  return <HiOutlineDocumentText className="w-5 h-5" />
}

function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 Bytes'
  if (isNaN(bytes) || bytes < 0) return 'Unknown size'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  if (i >= sizes.length) return 'Size too large'
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

function formatDate(s) {
  return new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

// ───────── Page ─────────────────────────────────────────────────────────
export default function DocumentsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // eslint-disable-next-line no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams()
  const { isWorkspaceSwitched, ownerAccountId, switchedRole } = useAuth()
  const { showSuccess, showError } = useAlerts()
  const { canCreate, canUpdate, canDelete, hasPermission, userRole } = usePermissionCheck()
  const { getEntity } = useWorkspace()

  const [documents, setDocuments] = useState([])
  const [documentGroups, setDocumentGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [selectedDocuments, setSelectedDocuments] = useState([])
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadModalGroupPreselection, setUploadModalGroupPreselection] = useState(null)
  const [viewMode, setViewMode] = useState('list')
  const [securityFilter, setSecurityFilter] = useState('all')
  const [entityFilter, setEntityFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [groupFilter, setGroupFilter] = useState('all')
  const [groupFilterSearch, setGroupFilterSearch] = useState('')
  const [showGroupFilterDropdown, setShowGroupFilterDropdown] = useState(false)
  const groupFilterDropdownRef = useRef(null)

  // Move-to-group modal
  const [showMoveModal, setShowMoveModal] = useState(false)
  const [selectedDocumentForMove, setSelectedDocumentForMove] = useState(null)
  const [targetGroups, setTargetGroups] = useState([])
  const [moveGroupSearch, setMoveGroupSearch] = useState('')

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false, title: '', message: '', onConfirm: null, type: 'warning',
  })

  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const itemsPerPage = 20

  const permissions = {
    canView:        hasPermission('document', 'read'),
    canEdit:        canUpdate('document'),
    canDelete:      canDelete('document'),
    canCreate:      canCreate('document'),
    canUpload:      canCreate('document'),
    canCreateGroup: canCreate('document_group'),
    canUpdateGroup: canUpdate('document_group'),
    canDeleteGroup: canDelete('document_group'),
    canMoveGroups:  canUpdate('document'),
  }

  const getDocumentSecurityAccessForRole = (documentSecurityLevel) => {
    const role = userRole?.toLowerCase() || 'silver'
    return getDocumentSecurityAccess(role, documentSecurityLevel, permissions)
  }

  // Click-outside for group-filter dropdown
  useEffect(() => {
    const onClickOutside = (e) => {
      if (groupFilterDropdownRef.current && !groupFilterDropdownRef.current.contains(e.target)) {
        setShowGroupFilterDropdown(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  // Always fetch groups for the filter dropdown
  useEffect(() => {
    fetchDocumentGroups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWorkspaceSwitched])

  useEffect(() => {
    fetchDocuments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWorkspaceSwitched, switchedRole, currentPage, searchQuery, securityFilter, entityFilter, groupFilter, typeFilter])

  async function fetchDocuments() {
    try {
      setLoading(true)
      const params = { page: currentPage, page_size: itemsPerPage, ordering: '-created_at' }
      if (searchQuery) params.search = searchQuery
      if (typeFilter !== 'all') params.type = typeFilter
      if (securityFilter !== 'all') params.security_level = securityFilter
      if (entityFilter === 'client')  params.client__isnull = 'false'
      if (entityFilter === 'case')    params.case__isnull = 'false'
      if (entityFilter === 'session') params.session__isnull = 'false'
      if (groupFilter !== 'all') params.group = groupFilter
      if (isWorkspaceSwitched && ownerAccountId) params.owner_account_id = ownerAccountId

      const response = await apiService.get('/documents/', { params })
      if (response.success) {
        const data = response.data.data || response.data
        const arr = data.data || data.results || []
        const pagination = data.pagination || {}
        // Filter by security-level access
        const accessible = arr.filter(doc => getDocumentSecurityAccessForRole(doc.security_level).canSee)
        setDocuments(accessible)
        const backendTotal = pagination.total_count || pagination.count || data.count
        if (backendTotal !== undefined) {
          setTotalCount(backendTotal)
          setTotalPages(Math.ceil(backendTotal / itemsPerPage))
        } else {
          setTotalCount(accessible.length)
          setTotalPages(Math.ceil(accessible.length / itemsPerPage))
        }
      } else {
        showError(response.error?.message || t('cases.loadFailed', { entity: getEntity('document').plural.toLowerCase() }))
      }
    } catch {
      showError(t('cases.loadFailed', { entity: getEntity('document').plural.toLowerCase() }))
    } finally {
      setLoading(false)
    }
  }

  async function fetchDocumentGroups() {
    try {
      const params = {}
      if (isWorkspaceSwitched && ownerAccountId) params.owner_account_id = ownerAccountId
      const response = await apiService.get('/document-groups/', { params })
      if (response.success) {
        const data = response.data
        setDocumentGroups(data.data || data.results || data || [])
      }
    } catch { /* swallow */ }
  }

  const toggleSelect = (id) => setSelectedDocuments(prev =>
    prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id])
  const toggleSelectAll = () => setSelectedDocuments(
    selectedDocuments.length === documents.length && documents.length > 0
      ? []
      : documents.map(d => d.id))

  async function handleDeleteDocument(documentId) {
    try {
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.delete(`/documents/${documentId}/`, { params })
      if (response.success) {
        showSuccess(t('documents.deleteSuccess', { entity: getEntity('document').singular }))
        fetchDocuments()
        setSelectedDocuments(prev => prev.filter(id => id !== documentId))
      } else {
        showError(response.error?.message || t('documents.deletedFailed', { entity: getEntity('document').singular.toLowerCase() }))
      }
    } catch {
      showError(t('documents.deletedFailed', { entity: getEntity('document').singular.toLowerCase() }))
    }
  }

  async function handleBulkDelete() {
    try {
      await Promise.all(selectedDocuments.map(documentId => {
        const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
        return apiService.delete(`/documents/${documentId}/`, { params })
      }))
      showSuccess(t('documents.deleteBulkSuccess', { count: selectedDocuments.length, entity: getEntity('document').plural.toLowerCase() }))
      setSelectedDocuments([])
      fetchDocuments()
    } catch {
      showError(t('documents.deletedFailed', { entity: getEntity('document').plural.toLowerCase() }))
    }
  }

  const confirmDeleteDocument = (doc) => {
    setConfirmDialog({
      isOpen: true,
      title: t('documents.deleteTitle', { entity: getEntity('document').singular }),
      message: t('documents.deleteMessage', { name: doc.file_name || doc.title || '—' }),
      type: 'danger',
      confirmText: t('common.delete'),
      onConfirm: () => {
        handleDeleteDocument(doc.id)
        setConfirmDialog(prev => ({ ...prev, isOpen: false }))
      },
    })
  }

  const confirmBulkDelete = () => {
    setConfirmDialog({
      isOpen: true,
      title: t('documents.deleteTitle', { entity: getEntity('document').plural }),
      message: t('documents.deleteManyMessage', {
        count: selectedDocuments.length,
        entity: selectedDocuments.length === 1
          ? getEntity('document').singular.toLowerCase()
          : getEntity('document').plural.toLowerCase(),
      }),
      type: 'danger',
      confirmText: t('common.delete'),
      onConfirm: () => {
        handleBulkDelete()
        setConfirmDialog(prev => ({ ...prev, isOpen: false }))
      },
    })
  }

  async function handleViewDocument(doc) {
    try {
      if (!doc?.secure_view_url) { showError(t('documents.viewFailed')); return }
      const token = tokenStorage.getToken()
      if (!token) { showError(t('documents.viewFailed')); return }
      const response = await fetch(doc.secure_view_url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/pdf' },
        credentials: 'include',
      })
      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        window.open(url, '_blank')
        setTimeout(() => window.URL.revokeObjectURL(url), 1000)
      } else {
        showError(t('documents.viewFailed'))
      }
    } catch {
      showError(t('documents.viewFailed'))
    }
  }

  async function handleDownloadDocument(doc) {
    try {
      if (!doc?.secure_download_url) { showError(t('documents.downloadFailed')); return }
      const token = tokenStorage.getToken()
      if (!token) { showError(t('documents.downloadFailed')); return }
      const response = await fetch(doc.secure_download_url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      })
      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = doc.file_name || 'document'
        document.body.appendChild(link); link.click(); document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        showSuccess(t('documents.downloadSuccess'))
      } else {
        showError(t('documents.downloadFailed'))
      }
    } catch {
      showError(t('documents.downloadFailed'))
    }
  }

  async function handleMoveDocumentToGroup(doc) {
    try {
      if (!permissions.canMoveGroups) {
        showError(t('common.permissionDenied'))
        return
      }
      const params = { page: 'all' }
      if (isWorkspaceSwitched && ownerAccountId) params.owner_account_id = ownerAccountId
      const response = await apiService.get('/document-groups/', { params })
      if (response.success) {
        const data = response.data
        const available = Array.isArray(data) ? data : (data.data || data.results || [])
        setTargetGroups(available)
        setSelectedDocumentForMove(doc)
        setMoveGroupSearch('')
        setShowMoveModal(true)
      } else {
        showError(t('cases.loadFailed', { entity: getEntity('document').singular.toLowerCase() }))
      }
    } catch {
      showError(t('cases.loadFailed', { entity: getEntity('document').singular.toLowerCase() }))
    }
  }

  async function executeMoveDocument(targetGroupId) {
    try {
      if (!selectedDocumentForMove) return
      const moveResponse = await apiService.patch(
        `/documents/${selectedDocumentForMove.id}/move-to-group/`,
        { group_id: targetGroupId === 'ungrouped' ? null : parseInt(targetGroupId) },
        { params: isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {} },
      )
      if (moveResponse.success) {
        showSuccess(t('documents.moveSuccess', { entity: getEntity('document').singular }))
        setShowMoveModal(false)
        setSelectedDocumentForMove(null)
        fetchDocuments()
      } else {
        showError(moveResponse.error?.message || t('documents.moveFailed', { entity: getEntity('document').singular.toLowerCase() }))
      }
    } catch {
      showError(t('documents.moveFailed', { entity: getEntity('document').singular.toLowerCase() }))
    }
  }

  if (loading && documents.length === 0) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <div className="h-6 w-1/3 bg-muted rounded animate-pulse" />
          <div className="h-4 w-1/2 bg-muted rounded animate-pulse" />
        </div>
        <Card padding="md">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />
            ))}
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <StrandedFilesBanner />

      <PageHeader
        icon={HiOutlineDocumentText}
        iconTone="brand"
        title={t('documents.title', { entity: getEntity('document').plural })}
        subtitle={
          isWorkspaceSwitched
            ? t('documents.subtitleSwitched', { entity: getEntity('document').plural.toLowerCase() })
            : t('documents.subtitle', { entity: getEntity('document').plural.toLowerCase() })
        }
        actions={
          permissions.canUpload && (
            <BlockedAction action="upload_document">
              <Button leadingIcon={HiOutlinePlus} onClick={() => setShowUploadModal(true)}>
                {t('documents.upload')}
              </Button>
            </BlockedAction>
          )
        }
      />

      <Card padding="none">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 p-4 sm:p-5 border-b border-border-subtle">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <form
              onSubmit={(e) => { e.preventDefault(); setCurrentPage(1); fetchDocuments() }}
              className="flex-1 max-w-lg"
            >
              <Input
                leadingIcon={HiOutlineMagnifyingGlass}
                placeholder={t('documents.searchPlaceholder', { entity: getEntity('document').plural.toLowerCase() })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                trailingAction={
                  searchQuery ? (
                    <IconButton
                      icon={HiOutlineXMark}
                      aria-label={t('common.clearSearch')}
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSearchQuery(''); setCurrentPage(1) }}
                    />
                  ) : null
                }
              />
            </form>

            <div className="flex flex-wrap items-end gap-2">
              <FilterSelect label={t('filters.byType')} value={typeFilter} onChange={(v) => { setTypeFilter(v); setCurrentPage(1) }}>
                <option value="all">{t('documents.types.all')}</option>
                <option value="document">{t('documents.types.documents')}</option>
                <option value="text,voice">{t('documents.types.messages')}</option>
                <option value="text">{t('documents.types.text')}</option>
                <option value="voice">{t('documents.types.voice')}</option>
              </FilterSelect>

              <FilterSelect label={t('filters.bySource')} value={entityFilter} onChange={(v) => { setEntityFilter(v); setCurrentPage(1) }}>
                <option value="all">{t('filters.allSources')}</option>
                <option value="client">{t('filters.viaClient', { entity: getEntity('client').singular })}</option>
                <option value="case">{t('filters.viaCase', { entity: getEntity('case').singular })}</option>
                <option value="session">{t('filters.viaSession', { entity: getEntity('session').singular })}</option>
              </FilterSelect>

              <FilterSelect label={t('filters.byLevel')} value={securityFilter} onChange={(v) => { setSecurityFilter(v); setCurrentPage(1) }}>
                <option value="all">{t('filters.allLevels')}</option>
                <option value="green">{t('documents.security.general')}</option>
                <option value="yellow">{t('documents.security.restricted')}</option>
                <option value="red">{t('documents.security.confidential')}</option>
              </FilterSelect>

              {/* Group filter dropdown */}
              <div ref={groupFilterDropdownRef} className="relative">
                <label className="block text-[10px] font-semibold text-ink-muted uppercase tracking-wider mb-1">{t('filters.byGroup')}</label>
                <button
                  type="button"
                  onClick={() => setShowGroupFilterDropdown(v => !v)}
                  className="h-10 px-3 inline-flex items-center gap-2 min-w-[180px] bg-surface text-ink-primary border border-border hover:border-border-strong focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand rounded-lg transition-colors"
                >
                  <HiOutlineFolder className="w-4 h-4 text-ink-muted flex-shrink-0" />
                  <span className="truncate text-sm flex-1 text-start">
                    {groupFilter === 'all'
                      ? t('filters.allGroups')
                      : documentGroups.find(g => String(g.id) === String(groupFilter))?.name || t('documents.groups.searchPlaceholder', { entity: getEntity('document').singular.toLowerCase() })}
                  </span>
                </button>
                {showGroupFilterDropdown && (
                  <div className="absolute z-50 mt-1 w-72 bg-elevated border border-border rounded-xl shadow-token-lg end-0">
                    <div className="p-2 border-b border-border-subtle">
                      <Input
                        leadingIcon={HiOutlineMagnifyingGlass}
                        size="sm"
                        value={groupFilterSearch}
                        onChange={(e) => setGroupFilterSearch(e.target.value)}
                        placeholder={t('documents.groups.searchGroupsPlaceholder')}
                        autoFocus
                      />
                    </div>
                    <div className="max-h-56 overflow-y-auto py-1">
                      <button
                        type="button"
                        onClick={() => { setGroupFilter('all'); setShowGroupFilterDropdown(false); setGroupFilterSearch('') }}
                        className={`w-full text-start px-3 py-2 text-sm hover:bg-muted transition-colors ${
                          groupFilter === 'all' ? 'bg-brand-soft text-brand font-medium' : 'text-ink-primary'
                        }`}
                      >
                        {t('filters.allGroups')}
                      </button>
                      {documentGroups
                        .filter(g => g.name.toLowerCase().includes(groupFilterSearch.toLowerCase()))
                        .map(group => (
                          <button
                            key={group.id}
                            type="button"
                            onClick={() => { setGroupFilter(String(group.id)); setShowGroupFilterDropdown(false); setGroupFilterSearch('') }}
                            className={`w-full text-start px-3 py-2 text-sm hover:bg-muted transition-colors ${
                              String(groupFilter) === String(group.id) ? 'bg-brand-soft text-brand font-medium' : 'text-ink-primary'
                            }`}
                          >
                            <span className="inline-flex items-center gap-2">
                              <HiOutlineFolder className="w-4 h-4 text-ink-muted" />
                              {group.name}
                            </span>
                          </button>
                        ))}
                      {documentGroups.filter(g => g.name.toLowerCase().includes(groupFilterSearch.toLowerCase())).length === 0 && (
                        <div className="px-3 py-3 text-sm text-ink-muted text-center">{t('common.noResults')}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* View toggle */}
              <div className="inline-flex border border-border rounded-lg overflow-hidden h-10 self-end">
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={`px-3 text-sm font-medium transition-colors ${
                    viewMode === 'list' ? 'bg-brand-soft text-brand' : 'text-ink-secondary hover:bg-muted'
                  }`}
                >
                  {t('common.viewList')}
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('grid')}
                  className={`px-3 text-sm font-medium transition-colors ${
                    viewMode === 'grid' ? 'bg-brand-soft text-brand' : 'text-ink-secondary hover:bg-muted'
                  }`}
                >
                  {t('common.viewGrid')}
                </button>
              </div>
            </div>
          </div>

          {selectedDocuments.length > 0 && (
            <div className="flex items-center gap-2 ps-3 border-s border-border-subtle">
              <Badge tone="brand">{t('common.selected', { count: selectedDocuments.length })}</Badge>
              {permissions.canDelete && (
                <Button variant="danger" size="sm" leadingIcon={HiOutlineTrash} onClick={confirmBulkDelete}>
                  {t('common.delete')}
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="relative">
          {loading && (
            <div className="absolute inset-0 bg-surface/70 backdrop-blur-sm flex items-center justify-center z-10">
              <Spinner />
            </div>
          )}

          {documents.length === 0 && !loading ? (
            <EmptyState
              icon={HiOutlineDocumentText}
              title={t('documents.noFound', { entity: getEntity('document').plural.toLowerCase() })}
              description={
                searchQuery
                  ? t('documents.noMatchSearch', { entity: getEntity('document').plural.toLowerCase(), query: searchQuery })
                  : permissions.canUpload
                    ? t('documents.getStarted', { entity: getEntity('document').singular.toLowerCase() })
                    : t('documents.noAvailable', { entity: getEntity('document').plural.toLowerCase() })
              }
              action={
                !searchQuery && permissions.canUpload && (
                  <BlockedAction action="upload_document">
                    <Button leadingIcon={HiOutlinePlus} onClick={() => setShowUploadModal(true)}>
                      {t('documents.uploadFirst', { entity: getEntity('document').singular.toLowerCase() })}
                    </Button>
                  </BlockedAction>
                )
              }
            />
          ) : documents.length > 0 ? (
            <>
              <div className="px-4 sm:px-5 py-2.5 border-b border-border-subtle bg-muted/40">
                <Checkbox
                  checked={selectedDocuments.length === documents.length && documents.length > 0}
                  onChange={toggleSelectAll}
                >
                  {t('cases.selectAll', {
                    count: documents.length,
                    entity: documents.length !== 1 ? getEntity('document').plural.toLowerCase() : getEntity('document').singular.toLowerCase(),
                  })}
                </Checkbox>
              </div>

              <div className="p-4 sm:p-5">
                <div className={`grid gap-3 ${
                  viewMode === 'grid'
                    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                    : 'grid-cols-1 lg:grid-cols-2'
                }`}>
                  {documents.map((doc) => (
                    <DocumentCard
                      key={doc.id}
                      doc={doc}
                      selected={selectedDocuments.includes(doc.id)}
                      onToggleSelect={() => toggleSelect(doc.id)}
                      onView={() => handleViewDocument(doc)}
                      onDownload={() => handleDownloadDocument(doc)}
                      onMoveToGroup={() => handleMoveDocumentToGroup(doc)}
                      onDelete={() => confirmDeleteDocument(doc)}
                      onDetails={() => navigate(`/documents/${doc.id}`)}
                      onPlay={() => doc.voice_stream_url && window.open(doc.voice_stream_url, '_blank')}
                      securityAccess={getDocumentSecurityAccessForRole(doc.security_level)}
                      permissions={permissions}
                      getEntity={getEntity}
                    />
                  ))}
                </div>
              </div>

              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 sm:p-5 border-t border-border-subtle bg-muted/30">
                  <p className="text-sm text-ink-muted">
                    {t('pagination.showing')}{' '}
                    <span className="font-semibold text-ink-primary">
                      {((currentPage - 1) * itemsPerPage) + 1}
                    </span>{' '}
                    –{' '}
                    <span className="font-semibold text-ink-primary">
                      {Math.min(currentPage * itemsPerPage, totalCount)}
                    </span>{' '}
                    {t('pagination.of')} <span className="font-semibold text-ink-primary">{totalCount}</span>
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      leadingIcon={HiOutlineChevronLeft}
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      className="rtl:[&_svg]:rotate-180"
                    >
                      {t('common.previous')}
                    </Button>
                    <span className="text-sm text-ink-muted px-2 tabular-nums">
                      {t('pagination.page')} <span className="font-semibold text-ink-primary">{currentPage}</span> {t('pagination.of')} <span className="font-semibold text-ink-primary">{totalPages}</span>
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      trailingIcon={HiOutlineChevronRight}
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      className="rtl:[&_svg]:rotate-180"
                    >
                      {t('common.next')}
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      </Card>

      <DocumentUploadModal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false)
          setUploadModalGroupPreselection(null)
        }}
        onUploadSuccess={() => {
          fetchDocuments()
          setShowUploadModal(false)
          setUploadModalGroupPreselection(null)
        }}
        preselectedGroup={uploadModalGroupPreselection}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
      />

      <MoveDocumentModal
        isOpen={showMoveModal}
        onClose={() => { setShowMoveModal(false); setMoveGroupSearch('') }}
        document={selectedDocumentForMove}
        groups={targetGroups}
        search={moveGroupSearch}
        onSearchChange={setMoveGroupSearch}
        onPick={executeMoveDocument}
        getEntity={getEntity}
      />
    </div>
  )
}

// ───────── Sub-components ───────────────────────────────────────────────

function FilterSelect({ label, value, onChange, children }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold text-ink-muted uppercase tracking-wider mb-1">{label}</label>
      <Select value={value} onChange={(e) => onChange(e.target.value)} className="min-w-[140px]">
        {children}
      </Select>
    </div>
  )
}

function DocumentCard({ doc, selected, onToggleSelect, onView, onDownload, onMoveToGroup, onDelete, onDetails, onPlay, securityAccess, permissions, getEntity }) {
  const { t } = useTranslation()
  const typeClass = TYPE_TONE[doc.type] || TYPE_TONE.document

  return (
    <div className="bg-surface border border-border rounded-xl p-4 hover:border-border-strong hover:shadow-token-sm transition-all flex flex-col h-full">
      <div className="flex items-start gap-3 mb-3">
        <div className="pt-1">
          <Checkbox checked={selected} onChange={onToggleSelect} aria-label="Select document" />
        </div>
        <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${typeClass}`}>
          <DocumentTypeIcon type={doc.type} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-ink-primary truncate" title={doc.title || doc.file_name || 'Untitled'}>
              {doc.title || doc.file_name || (doc.type === 'text' ? 'Text message' : doc.type === 'voice' ? 'Voice message' : 'Untitled')}
            </h3>
            {doc.storage_provider && doc.storage_provider !== 'local' && (
              <Badge tone="info" size="xs" variant="outline">
                {doc.storage_provider === 'google_drive' ? 'Drive' : doc.storage_provider === 'onedrive' ? 'OneDrive' : doc.storage_provider === 'dropbox' ? 'Dropbox' : doc.storage_provider}
              </Badge>
            )}
            {doc.is_read === false && (
              <span className="w-2 h-2 bg-brand rounded-full flex-shrink-0 mt-1.5" title="Unread" />
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap mt-1">
            {doc.message_scope === 'broadcast' && (
              <Badge tone="warning" size="xs">Broadcast</Badge>
            )}
            <SecurityBadge level={doc.security_level} />
          </div>
          {doc.type === 'document' && doc.file_name && (
            <p className="text-xs text-ink-muted break-words mt-1">
              {doc.file_name} · {formatFileSize(doc.file_size)}
            </p>
          )}
          {doc.type === 'text' && doc.content && (
            <p className="text-xs text-ink-muted truncate mt-1">
              {doc.content.substring(0, 80)}{doc.content.length > 80 ? '…' : ''}
            </p>
          )}
          {doc.type === 'voice' && (
            <p className="text-xs text-ink-muted mt-1">Voice message</p>
          )}
          {doc.created_by_name && (
            <p className="text-[11px] text-ink-muted mt-1">by {doc.created_by_name}</p>
          )}
        </div>
      </div>

      <dl className="space-y-1.5 text-sm text-ink-secondary mb-3 flex-1">
        {doc.client_name && (
          <div className="flex items-center gap-2">
            <HiOutlineUser className="w-4 h-4 text-ink-muted" />
            <span className="truncate">{doc.client_name}</span>
          </div>
        )}
        {doc.case_title && (
          <div className="flex items-center gap-2">
            <HiOutlineFolder className="w-4 h-4 text-ink-muted" />
            <span className="truncate">{doc.case_title}</span>
          </div>
        )}
        {doc.session_title && (
          <div className="flex items-center gap-2">
            <HiOutlineCalendar className="w-4 h-4 text-ink-muted" />
            <span className="truncate">{doc.session_title}</span>
          </div>
        )}
        {doc.group_name && (
          <div className="inline-flex items-center gap-1.5 text-brand bg-brand-soft px-2 py-0.5 rounded-md text-xs">
            <HiOutlineFolder className="w-3.5 h-3.5" />
            <span className="truncate font-medium">{doc.group_name}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <HiOutlineClock className="w-4 h-4 text-ink-muted" />
          <span>{formatDate(doc.created_at)}</span>
        </div>
      </dl>

      <div className="flex items-center justify-between pt-3 border-t border-border-subtle mt-auto">
        <div className="flex items-center gap-1">
          {doc.type === 'document' && (
            <>
              <IconButton
                icon={HiOutlineEye}
                aria-label={securityAccess.canView ? 'View' : securityAccess.restrictionMessage}
                variant="ghost"
                size="sm"
                disabled={!securityAccess.canView}
                onClick={onView}
              />
              <IconButton
                icon={HiOutlineArrowDownTray}
                aria-label={securityAccess.canDownload ? 'Download' : securityAccess.restrictionMessage}
                variant="ghost"
                size="sm"
                disabled={!securityAccess.canDownload}
                onClick={onDownload}
              />
            </>
          )}
          {doc.type === 'voice' && doc.voice_stream_url && (
            <IconButton
              icon={HiOutlineSpeakerWave}
              aria-label="Play voice message"
              variant="ghost"
              size="sm"
              onClick={onPlay}
            />
          )}
          {doc.type === 'text' && doc.secure_download_url && securityAccess.canDownload && (
            <IconButton
              icon={HiOutlineArrowDownTray}
              aria-label="Download as .txt"
              variant="ghost"
              size="sm"
              onClick={onDownload}
            />
          )}
          {(doc.type === 'text' || doc.type === 'voice') && (
            <Badge tone={doc.is_read ? 'success' : 'info'} size="xs">
              {doc.is_read ? t('common.read') : t('common.unread')}
            </Badge>
          )}
          {(doc.type === 'text' || doc.type === 'voice') && doc.read_count > 0 && (
            <span className="text-xs text-ink-muted ms-1">{doc.read_count} {t('common.read').toLowerCase()}</span>
          )}
          {permissions.canMoveGroups && (
            <IconButton
              icon={HiOutlineFolder}
              aria-label={doc.group ? t('documents.groups.moveToGroup') : t('documents.groups.joinGroup')}
              variant="ghost"
              size="sm"
              onClick={onMoveToGroup}
            />
          )}
          {securityAccess.canDelete && (
            <IconButton
              icon={HiOutlineTrash}
              aria-label={t('common.delete')}
              variant="danger"
              size="sm"
              onClick={onDelete}
            />
          )}
        </div>
        <Button variant="link" size="sm" onClick={onDetails}>
          {t('common.details')}
        </Button>
      </div>
    </div>
  )
}

function MoveDocumentModal({ isOpen, onClose, document: doc, groups, search, onSearchChange, onPick, getEntity }) {
  const { t } = useTranslation()
  if (!isOpen || !doc) return null
  const isInGroup = !!doc.group
  const filtered = groups.filter(g => g.name.toLowerCase().includes(search.toLowerCase()))
  const displayGroups = isInGroup ? filtered.filter(g => g.id !== doc.group) : filtered

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isInGroup ? t('documents.groups.moveToGroup') : t('documents.groups.joinGroup')}
      size="sm"
    >
      <Modal.Body>
        <p className="text-xs text-ink-muted mb-3 truncate">{doc.title || doc.file_name}</p>

        {isInGroup && (
          <button
            type="button"
            onClick={() => onPick('ungrouped')}
            className="w-full text-start flex items-center gap-3 px-3 py-2.5 mb-3 rounded-lg border border-danger/30 hover:bg-danger-soft transition-colors group/remove"
          >
            <span className="w-7 h-7 bg-danger-soft text-danger rounded-md flex items-center justify-center flex-shrink-0">
              <HiOutlineXMark className="w-3.5 h-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-danger">{t('documents.groups.removeFromGroup')}</div>
              <div className="text-[11px] text-ink-muted">{t('documents.groups.ungroupDescription', { entity: getEntity('document').singular.toLowerCase() })}</div>
            </div>
          </button>
        )}

        {groups.length === 0 ? (
          <EmptyState
            icon={HiOutlineFolder}
            title={t('documents.groups.noGroupsAvailable')}
            description={t('documents.groups.noGroupsHelp')}
            compact
          />
        ) : (
          <>
            {groups.length > 4 && (
              <div className="mb-2">
                <Input
                  leadingIcon={HiOutlineMagnifyingGlass}
                  size="sm"
                  placeholder={t('documents.groups.searchGroupsPlaceholder')}
                  value={search}
                  onChange={(e) => onSearchChange(e.target.value)}
                  autoFocus
                />
              </div>
            )}
            <div className="max-h-56 overflow-y-auto -mx-1 px-1 space-y-1">
              {displayGroups.length === 0 ? (
                <p className="text-xs text-ink-muted text-center py-4">{t('common.noResults')}</p>
              ) : (
                displayGroups.map(group => (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => onPick(group.id)}
                    className="w-full text-start px-3 py-2 rounded-lg hover:bg-muted transition-colors flex items-center gap-2.5 group/item"
                  >
                    <span className="w-7 h-7 bg-info-soft text-info rounded-md flex items-center justify-center flex-shrink-0">
                      <HiOutlineFolder className="w-3.5 h-3.5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-ink-primary truncate group-hover/item:text-brand transition-colors">
                        {group.name}
                      </div>
                      {group.description && (
                        <div className="text-[11px] text-ink-muted truncate">{group.description}</div>
                      )}
                    </div>
                    <HiOutlineChevronRight className="w-3.5 h-3.5 text-ink-muted flex-shrink-0 rtl:rotate-180" />
                  </button>
                ))
              )}
            </div>
            <p className="text-[11px] text-ink-muted text-center mt-3 pt-3 border-t border-border-subtle">
              {t('documents.groups.groupCount', { count: displayGroups.length })}
            </p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
      </Modal.Footer>
    </Modal>
  )
}
